import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GeoJSON, useMap } from 'react-leaflet';
import type { GeoJSON as LeafletGeoJSON, PathOptions, LeafletMouseEvent } from 'leaflet';
import type { Feature } from 'geojson';
import { useQuery } from '@tanstack/react-query';
import type { Layer } from 'leaflet';
import { queries, topoIstat, topoProvAcr, topoRegCode, topoDistCode, topoDistName, topoName } from '@/data';
import { activeMapCountryConfig } from '@/data/active-country';
import { mapLevel } from '@/features/map-core/countries';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';
import { useMatchSet } from '@/features/filters';
import { useDetailsStore } from '@/features/details';
import { emit } from '@/lib/events';
import { useIsMobile } from '@/lib/useMediaQuery';
import { useMapStore } from '@/features/map-core/store';
import { useChoroplethStore } from './store';
import { useChoroplethView } from './useChoroplethView';
import { LAYERS_BY_ID } from './layers-config';
import { calcComuneStyle, calcProvStyle, calcRegionStyle, calcDistrictStyle, hatchFillOpacity } from './style';
import { NO_DATA_COLOR } from './colors';
import { comuneTooltipHtml, provinceTooltipHtml, regionTooltipHtml, districtTooltipHtml } from './tooltips';
import { HatchPatternDefs, setHatchColor } from './HatchPatternDefs';
import { uiLang } from '@/i18n/lang';

const HOVER_CLASS = 'choropleth-hovered';
// ink-900 — нейтральный тёмный, контрастен на всех climate-палитрах
// (тёплые altitude/t_jul тоже не конфликтуют, как было с terra-600).
const LOCKED_COLOR = '#1c1a16';
const HOVER_COLOR = '#3a3730'; // ink-700 — на тон светлее SELECTED
const LOCKED_STYLE: PathOptions = {
  weight: 2.5,
  color: LOCKED_COLOR,
  opacity: 1,
  dashArray: undefined,
};

const FALLBACK_STYLE: PathOptions = {
  fillColor: NO_DATA_COLOR,
  fillOpacity: 0.4,
  weight: 1,
  color: '#666',
  opacity: 0.5,
};

export function ChoroplethLayer() {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const { activeLayer, mode, overlayHidden } = useChoroplethStore();
  const view = useChoroplethView();
  const matchSet = useMatchSet();
  const zoom = useMapStore((s) => s.zoom);
  const geojsonRef = useRef<LeafletGeoJSON | null>(null);
  const isMobile = useIsMobile();
  const map = useMap();
  // US-02: единый источник правды о текущем hover'е. Leaflet иногда
  // теряет mouseout при быстром переходе между полигонами — поэтому в
  // mouseover мы сначала чистим предыдущий, и только потом ставим новый.
  // Если mouseout всё-таки придёт позже — он работает как страховка.
  // key = istat (comuni view) или dist_code (districts view) — единый ключ
  // ховера для обоих кликабельных уровней.
  const hoveredRef = useRef<{ layer: Layer; key: string } | null>(null);

  const { data: meta } = useQuery(queries.meta());
  const version = meta?.version ?? '';

  const { data: fullIndex } = useQuery({
    ...queries.fullIndex(version),
    enabled: !!version,
  });

  // TopoLevel 1:1 матчит ChoroplethView — лоадер уже умеет regions/provinces/comuni.
  const topoLevel = view;
  const { data: topo } = useQuery({
    ...queries.topo(topoLevel, version),
    enabled: !!version,
  });

  // Имена свойств внутри TopoJSON — из реестра страны, а не из констант.
  // levelDef === undefined означает, что у страны этого уровня нет; тогда
  // полигоны не окрашиваются и не биндят тултип, вместо того чтобы молча
  // читать итальянское имя свойства и получать null.
  const levelDef = mapLevel(activeMapCountryConfig(), view);
  const codeProp = levelDef?.codeProp;
  const nameProp = levelDef?.nameProp;
  // Тултипы рендерятся сырым HTML вне дерева React, поэтому термин единицы
  // («comuni» / «municipios») подставляется здесь, а не в i18n-строке.
  const { unitPlural } = useUnitTerms();

  const layerDef = LAYERS_BY_ID[activeLayer];
  // Метка активного слоя на языке интерфейса — та же, что в переключателе.
  const layerLabel = layerDef ? t(`layer.label.${layerDef.id}`, { defaultValue: layerDef.label }) : '';

  const openIstat = useDetailsStore((s) => s.openIstat);
  const openDist = useDetailsStore((s) => s.openDist);

  const makeStyleFn = useCallback(() => {
    return (feature?: Feature): PathOptions => {
      if (!feature?.properties || !fullIndex || !layerDef || !codeProp) return FALLBACK_STYLE;
      const props = feature.properties as Record<string, unknown>;

      if (view === 'comuni') {
        const istat = topoIstat(props, codeProp);
        if (!istat) return FALLBACK_STYLE;
        return calcComuneStyle(istat, layerDef, matchSet, fullIndex.byIstat, zoom, openIstat, overlayHidden);
      }
      if (view === 'districts') {
        const dist = topoDistCode(props, codeProp);
        if (!dist) return FALLBACK_STYLE;
        return calcDistrictStyle(dist, layerDef, matchSet, fullIndex.byDist, zoom, overlayHidden);
      }
      if (view === 'provinces') {
        const prov = topoProvAcr(props, codeProp);
        if (!prov) return FALLBACK_STYLE;
        return calcProvStyle(prov, layerDef, matchSet, fullIndex.byProv, zoom, overlayHidden);
      }
      // regions
      const reg = topoRegCode(props, codeProp);
      if (!reg) return FALLBACK_STYLE;
      return calcRegionStyle(reg, layerDef, matchSet, fullIndex.byReg, zoom, overlayHidden);
    };
  }, [layerDef, view, matchSet, fullIndex, zoom, openIstat, overlayHidden, codeProp]);

  // styleFnRef держит АКТУАЛЬНУЮ style-функцию. Используется в hover-handler'ах
  // вместо Leaflet's `resetStyle()` — у того есть давний баг: он применяет
  // `this.options.style` (зафиксированную на момент mount GeoJSON), а не
  // последнюю переданную в `setStyle()`. Без ref'а mouseout восстанавливает
  // СТАРЫЕ цвета при смене zoom/mode/layer.
  const styleFnRef = useRef<(feature?: Feature) => PathOptions>(() => FALLBACK_STYLE);
  useEffect(() => {
    styleFnRef.current = makeStyleFn();
  }, [makeStyleFn]);

  // Update styles without remounting GeoJSON when only layer/matchSet changes
  useEffect(() => {
    geojsonRef.current?.setStyle(makeStyleFn());
  }, [makeStyleFn]);

  const openDetails = useDetailsStore((s) => s.open);
  const openDistrict = useDetailsStore((s) => s.openDistrict);
  const closeDetails = useDetailsStore((s) => s.close);

  const TOOLTIP_OPTS = {
    sticky: true,
    direction: 'top' as const,
    opacity: 0.95,
    className: 'choropleth-tooltip',
  };

  // Привязать tooltip к одному leaflet-слою на основе feature properties + текущего layerDef
  const bindFeatureTooltip = useCallback(
    (sublayer: Layer, feature: Feature | undefined) => {
      // Mobile: tooltips are hover-only — on touch they never fire. Skip the
      // bind entirely so we don't pay ~8000 × bindTooltip cost on the
      // provinces→comuni view switch at zoom 10. Touch users go through
      // click → DetailsPanel instead.
      if (isMobile) return;
      const tl = sublayer as Layer & {
        unbindTooltip?: () => unknown;
        bindTooltip?: (
          content: string,
          options?: Record<string, unknown>,
        ) => unknown;
      };
      if (typeof tl.bindTooltip !== 'function') return;
      tl.unbindTooltip?.();
      const props = feature?.properties;
      if (!props || !fullIndex || !layerDef || !codeProp) return;
      const propsRec = props as Record<string, unknown>;
      if (view === 'comuni') {
        const istat = topoIstat(propsRec, codeProp);
        if (!istat) return;
        const c = fullIndex.byIstat.get(istat);
        if (!c) return;
        tl.bindTooltip(comuneTooltipHtml(c, layerDef, lang, layerLabel), TOOLTIP_OPTS);
      } else if (view === 'districts') {
        const dist = topoDistCode(propsRec, codeProp);
        if (!dist) return;
        const list = fullIndex.byDist.get(dist);
        if (!list || list.length === 0) return;
        // topoDistName склеивает двуязычное имя Южного Тироля; у страны без
        // такого случая имя берётся по nameProp из реестра уровня.
        const name = topoDistName(propsRec) ?? topoName(propsRec, nameProp) ?? dist;
        tl.bindTooltip(districtTooltipHtml(name, list, layerDef, lang, unitPlural, layerLabel), TOOLTIP_OPTS);
      } else if (view === 'provinces') {
        const prov = topoProvAcr(propsRec, codeProp);
        if (!prov) return;
        const list = fullIndex.byProv.get(prov);
        if (!list || list.length === 0) return;
        // Заголовок — название провинции, если оно есть в TopoJSON. Код в роли
        // заголовка читается как «28»: у Италии акроним провинции ещё узнаваем
        // («TO»), у Испании cpro — просто две цифры.
        const label = topoName(propsRec, nameProp) ?? prov;
        tl.bindTooltip(provinceTooltipHtml(label, list, layerDef, lang, unitPlural, layerLabel), TOOLTIP_OPTS);
      } else {
        const reg = topoRegCode(propsRec, codeProp);
        if (!reg) return;
        const name = topoName(propsRec, nameProp) ?? reg;
        const list = fullIndex.byReg.get(reg);
        if (!list || list.length === 0) return;
        tl.bindTooltip(regionTooltipHtml(name, list, layerDef, lang, unitPlural, layerLabel), TOOLTIP_OPTS);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, layerDef, fullIndex, isMobile, lang, codeProp, nameProp, unitPlural, layerLabel],
  );

  // Очистить hover у указанного слоя; восстановить LOCKED_STYLE если это
  // currently-locked comune.
  const clearHoverOn = useCallback((entry: { layer: Layer; key: string }) => {
    const path = entry.layer as Layer & {
      getElement: () => SVGPathElement | null;
      setStyle: (s: PathOptions) => void;
      bringToFront: () => void;
      feature?: Feature;
    };
    const el = path.getElement();
    el?.classList.remove(HOVER_CLASS);
    // Применяем АКТУАЛЬНУЮ style-функцию вместо resetStyle (см. styleFnRef).
    path.setStyle(styleFnRef.current(path.feature));

    const ds = useDetailsStore.getState();
    const locked = ds.openIstat ?? ds.openDist;
    if (entry.key === locked) {
      path.setStyle(LOCKED_STYLE);
      path.bringToFront();
    }
  }, []);

  const applyHoverOn = useCallback((layer: Layer) => {
    const path = layer as Layer & {
      setStyle: (s: PathOptions) => void;
      bringToFront: () => void;
      getElement: () => SVGPathElement | null;
      options: PathOptions;
    };
    const baseColor = path.options.fillColor;
    if (baseColor) setHatchColor(baseColor);
    const z = useMapStore.getState().zoom;
    // HOVER border = ink-700 2px (тоньше и светлее SELECTED ink-900 2.5px).
    // Hatch одинаковый с locked — различие именно в border (US-03).
    path.setStyle({
      weight: 2,
      color: HOVER_COLOR,
      opacity: 0.85,
      fillOpacity: hatchFillOpacity(z),
    });
    path.bringToFront();
    path.getElement()?.classList.add(HOVER_CLASS);
  }, []);

  const onEachFeature = useCallback(
    (feature: Feature, layer: Layer) => {
      layer.on('click', () => {
        if (!feature.properties || !codeProp) return;
        if (view === 'comuni') {
          const istat = topoIstat(feature.properties as Record<string, unknown>, codeProp);
          if (!istat) return;
          // Toggle behaviour: first click opens, second click on the same
          // comune closes the panel. Also close with × / backdrop / Escape.
          const currentOpen = useDetailsStore.getState().openIstat;
          if (currentOpen === istat) {
            closeDetails();
          } else {
            openDetails(istat);
            emit('comune:opened', { istat, source: 'choropleth' });
          }
          return;
        }
        if (view === 'districts') {
          // District (SLL / comunità comprensoriale) — открываем агрегированную
          // панель (как comune: погода/население/инфра + сохранение).
          // Toggle: повторный клик закрывает.
          const dist = topoDistCode(feature.properties as Record<string, unknown>, codeProp);
          if (!dist) return;
          const currentOpen = useDetailsStore.getState().openDist;
          if (currentOpen === dist) {
            closeDetails();
          } else {
            openDistrict(dist);
            emit('district:opened', { dist, source: 'choropleth' });
          }
          return;
        }
        // Region / province click — zoom-in drill-down so the user can keep
        // narrowing without manually wheel-zooming. Bounded so we don't fly
        // past the district/comune views. region → provinces (≤8),
        // province → districts (≤10).
        const path = layer as Layer & {
          getBounds?: () => import('leaflet').LatLngBounds;
        };
        if (typeof path.getBounds === 'function') {
          const bounds = path.getBounds();
          if (bounds && bounds.isValid()) {
            const targetMaxZoom = view === 'regions' ? 8 : 10;
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: targetMaxZoom, animate: true });
          }
        }
      });

      layer.on('mouseover', (e: LeafletMouseEvent) => {
        const props = feature.properties as Record<string, unknown> | undefined;
        // Ховер активен на кликабельных уровнях: comuni и districts.
        const key = !props || !codeProp
          ? null
          : view === 'comuni'
          ? topoIstat(props, codeProp)
          : view === 'districts'
          ? topoDistCode(props, codeProp)
          : null;
        if (!key) return;

        // US-03 DoD: HOVER+SELECTED → остаётся как SELECTED.
        // Locked feature уже отрисован с собственным стилем — hover не трогаем.
        const ds = useDetailsStore.getState();
        const locked = ds.openIstat ?? ds.openDist;
        if (key === locked) return;

        // US-02: если уже что-то висит в hover (Leaflet иногда теряет
        // mouseout при быстром движении) — сначала чистим предыдущее.
        const prev = hoveredRef.current;
        if (prev && prev.layer !== e.target) {
          clearHoverOn(prev);
        }
        applyHoverOn(e.target as Layer);
        hoveredRef.current = { layer: e.target as Layer, key };
      });

      layer.on('mouseout', (e: LeafletMouseEvent) => {
        // Чистим только если mouseout пришёл для actually-hovered слоя.
        // Если это «опоздавший» mouseout от уже-перезаписанного слоя —
        // игнорируем, иначе сотрём актуальный hover.
        const cur = hoveredRef.current;
        if (cur && cur.layer === e.target) {
          clearHoverOn(cur);
          hoveredRef.current = null;
        }
      });

      // Initial tooltip bind — гарантировано срабатывает при mount каждого feature,
      // даже если topo прилетает позже view (useEffect ниже не сработал бы — нет deps).
      bindFeatureTooltip(layer, feature);
    },
    [view, codeProp, openDetails, openDistrict, closeDetails, bindFeatureTooltip, clearHoverOn, applyHoverOn, map],
  );

  // US-02: курсор ушёл за пределы карты целиком → сбросить hover.
  // Mouseout у последнего полигона может не сработать в этом случае.
  useEffect(() => {
    const container = map.getContainer();
    const handleLeave = () => {
      const cur = hoveredRef.current;
      if (cur) {
        clearHoverOn(cur);
        hoveredRef.current = null;
      }
    };
    container.addEventListener('mouseleave', handleLeave);
    return () => container.removeEventListener('mouseleave', handleLeave);
  }, [map, clearHoverOn]);

  // Refresh tooltips при смене layerDef без remount GeoJSON.
  // Также страховка: пересобирает tooltips если по какой-то причине initial bind не отработал.
  useEffect(() => {
    const gj = geojsonRef.current;
    if (!gj) return;
    gj.eachLayer((sublayer: Layer) => {
      const tl = sublayer as Layer & { feature?: Feature };
      bindFeatureTooltip(sublayer, tl.feature);
    });
  }, [bindFeatureTooltip]);

  // Locked-feature highlight: thick dark border (LOCKED_STYLE) on the open
  // comune ИЛИ district, restore normal style on any previously locked one.
  useEffect(() => {
    const gj = geojsonRef.current;
    if (!gj || (view !== 'comuni' && view !== 'districts')) return;
    const lockedKey = view === 'comuni' ? openIstat : openDist;
    gj.eachLayer((sublayer: Layer) => {
      const tl = sublayer as Layer & {
        feature?: Feature;
        setStyle: (s: PathOptions) => void;
        bringToFront: () => void;
        getElement: () => SVGPathElement | null;
      };
      const props = tl.feature?.properties as Record<string, unknown> | undefined;
      const key =
        props && codeProp
          ? view === 'comuni'
            ? topoIstat(props, codeProp)
            : topoDistCode(props, codeProp)
          : null;
      if (key && key === lockedKey) {
        tl.setStyle(LOCKED_STYLE);
        tl.bringToFront();
      } else {
        // Не resetStyle (стейл-функция) — применяем актуальную.
        tl.setStyle(styleFnRef.current(tl.feature));
      }
    });
  }, [openIstat, openDist, view, topo, codeProp]);

  if (mode !== 'choropleth') return null;
  if (!topo || !fullIndex) return null;

  return (
    <>
      <HatchPatternDefs />
      <GeoJSON
        key={view}
        ref={geojsonRef}
        data={topo}
        style={makeStyleFn()}
        onEachFeature={onEachFeature}
      />
    </>
  );
}
